import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import { green } from '@material-ui/core/colors';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MUITextField, { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete';
import { Field, FieldProps, useFormikContext } from 'formik';
import { TextField as FormikMUITextField } from 'formik-material-ui';
import { Autocomplete } from 'formik-material-ui-lab';
import * as R from 'ramda';
import React, { useEffect, useRef } from 'react';
import { Experience, SkillArea, SkillType } from '../API';
import useTechSkills from '../hooks/useTechSkills';
import { EXPERIENCE_HUMAN_NAMES } from '../models/engineer';
import TechnicalSkill from '../models/technical-skills';

const useStyles = makeStyles((theme) => ({
  buttons: {
    marginTop: theme.spacing(2),
    '& > *': {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      display: 'flex',
      flexDirection: 'column',
      '& > *': {
        flex: 1,
      },
    },
  },
  chipsContainer: {
    marginTop: 0,
    paddingLeft: 0,
    justifyContent: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      flexDirection: 'column',
    },
  },
  chips: {
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: theme.typography.body1.fontSize,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      display: 'flex',
      textAlign: 'left',
      justifyContent: 'space-between',
    },
  },
  chipsPrimary: {
    color: green[600],
    borderColor: green[600],
    '&:hover, &:focus': {
      color: green[800],
      background: `${green[50]} !important`,
      borderColor: green[800],
    },
    '&:focus': {
    },
  },
  fileInput: {
    display: 'none',
  },
  fileFieldFilename: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
  },
}));

export function ExperienceField({
  label,
  ...otherParams
}: MuiTextFieldProps) {
  const emptyValue = label ? undefined : 'Select your experience';
  return (
    <Field
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...otherParams}
      label={label}
      component={FormikMUITextField}
      select
      SelectProps={{
        displayEmpty: true,
        renderValue: (value: keyof typeof Experience) => (
          <Typography>{EXPERIENCE_HUMAN_NAMES[value] || emptyValue}</Typography>
        ),
      }}
    >
      {R.map((experience) => (
        <MenuItem key={experience} value={experience}>
          <Typography>{EXPERIENCE_HUMAN_NAMES[experience]}</Typography>
        </MenuItem>
      ), Object.keys(Experience) as (keyof typeof Experience)[])}
    </Field>
  );
}

export function ToggableChipsField<T>({
  name,
  options,
  getOptionLabel = (option) => (option as any).label,
  getOptionId = (option) => (option as any).id,
  autoFocus,
}: {
  name: string
  options: T[],
  getOptionLabel?: (option: T) => string,
  getOptionId?: (option: T) => string,
  autoFocus?: boolean,
}) {
  const classes = useStyles();
  const chipsContainerRef = useRef<HTMLUListElement>(null!);
  useEffect(() => {
    if (autoFocus) {
      (chipsContainerRef.current.children[0] as HTMLUListElement)?.focus();
    }
  }, [autoFocus]);
  return (
    <Box>
      <Field
        name={name}
        displayEmpty
        autoWidth
      >
        {({ field, form }: FieldProps<T[], T[]>) => {
          const chipOptions = options.map((option) => ({
            label: getOptionLabel(option),
            id: getOptionId(option),
            selected: field.value.some((value) => getOptionId(value) === getOptionId(option)),
          }));
          return (
            <ul className={classes.chipsContainer} ref={chipsContainerRef}>
              {chipOptions.map((option) => {
                function handleClick() {
                  const matcher = (value: T) => getOptionId(value) === option.id;
                  const current = field.value.find(matcher);
                  const values = current
                    ? R.reject(matcher, field.value)
                    : [...field.value, options.find(matcher)];
                  form.setFieldValue(name, values);
                }
                return (
                  <Chip
                    component="li"
                    classes={{
                      root: classes.chips,
                      outlinedPrimary: classes.chipsPrimary,
                      deleteIconOutlinedColorPrimary: classes.chipsPrimary,
                    }}
                    key={option.id}
                    label={option.label}
                    clickable
                    color={option.selected ? 'primary' : 'default'}
                    deleteIcon={option.selected ? <CheckCircleIcon /> : <AddCircleIcon />}
                    onDelete={handleClick}
                    variant="outlined"
                    onClick={handleClick}
                    onKeyPress={handleClick}
                  />
                );
              })}
            </ul>
          );
        }}
      </Field>
    </Box>
  );
}

const UNCHECKED_CHECKBOX_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED_CHECKBOX_ICON = <CheckBoxIcon fontSize="small" />;

export function SkillsAutocompleteField(
  {
    area,
    type,
    label,
    name,
    helperText,
    margin,
    ...otherProps
  }:
  MuiTextFieldProps & {
    name: string,
    area?: SkillArea,
    type: SkillType,
  },
) {
  const {
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<any>();
  const { skills = [], fetchingSkills } = useTechSkills({ area, type });
  return (
    <>
      <Field
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...otherProps}
        name={name}
        component={Autocomplete}
        options={skills}
        getOptionLabel={(option: TechnicalSkill) => option.name}
        multiple
        autoComplete
        autoHighlight
        disableCloseOnSelect
        loading={fetchingSkills}
        getOptionSelected={
          (option: TechnicalSkill, value: TechnicalSkill) => option.id === value.id
        }
        renderInput={(params: AutocompleteRenderInputParams) => (
          <MUITextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            margin={margin}
            label={label}
            helperText={(touched[name] && errors[name]) || helperText}
            error={touched[name] && Boolean(errors[name])}
          />
        )}
        onChange={(event: any, value: any) => {
          setFieldTouched(name, true);
          setFieldValue(name, value);
        }}
        renderOption={(option: TechnicalSkill, { selected }: { selected: boolean }) => (
          <>
            <Checkbox
              color="primary"
              icon={UNCHECKED_CHECKBOX_ICON}
              checkedIcon={CHECKED_CHECKBOX_ICON}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.name}
          </>
        )}
      />
    </>
  );
}

export function TextField(props: MuiTextFieldProps) {
  return (
    <Field
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      component={FormikMUITextField}
    />
  );
}

export function FileField({
  name,
  label,
  fullWidth,
  margin,
  buttonText = 'Select File',
  currentFileUrl,
}: MuiTextFieldProps & { buttonText?: string, currentFileUrl?: string }) {
  const classes = useStyles();
  const id = `${name}-file-input`;
  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => (
        <FormControl
          fullWidth={fullWidth}
          error={Boolean(meta.touched && meta.error)}
          margin={margin}
        >
          <Box>
            {label && <InputLabel shrink>{label}</InputLabel>}
            <Box paddingTop={3}>
              <label htmlFor={id}>
                <Button variant="outlined" color="primary" component="span">
                  {buttonText}
                </Button>
                {field.value?.name
                  ? (
                    <Typography variant="body1" className={classes.fileFieldFilename}>{field.value.name}</Typography>
                  ) : (
                    currentFileUrl && (
                      <Typography variant="body1" className={classes.fileFieldFilename}>
                        <Link href={currentFileUrl} rel="noopener noreferrer">
                          Download existing file
                        </Link>
                      </Typography>
                    )
                  )}
                <input
                  className={classes.fileInput}
                  id={id}
                  type="file"
                  onChange={(event) => form.setFieldValue('cvFile', event.currentTarget.files?.[0])}
                />
              </label>
            </Box>
            {meta.error && meta.touched && <FormHelperText>{meta.error}</FormHelperText>}
          </Box>
        </FormControl>
      )}
    </Field>
  );
}

export function FormButtonsContainer({ children }: { children: React.ReactNode }) {
  const classes = useStyles();
  return (
    <Box className={classes.buttons}>
      {children}
    </Box>
  );
}

export function SubmitButton({ text }: { text: string }) {
  return <Button type="submit" color="primary" variant="contained">{text}</Button>;
}
